import {
  ArtistImageGallery,
  Grid,
  Icon,
  IconName,
  Lightbox,
} from '@design-system';
import { TrackMultiMediaClick } from '@utils/analytics';
import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import { storyblokMediaItemToLightboxEntry } from '@utils/lightbox';
import {
  isStoryblokRichTextPresent,
  renderStoryblokRichText,
} from '@utils/renderStoryblokRichText';
import { useTranslation } from 'next-i18next';
import React from 'react';
import {
  render,
  StoryblokRichtext,
} from 'storyblok-rich-text-react-renderer-ts';

import { ARTIST_PAGE_TYPE } from '../../../constants';
import { MultiMediaClickOptions } from '../../../types/Analytics';
import {
  ArtistGalleryImageStoryblok,
  ArtistOriginalWorksStoryblok,
  RichtextStoryblok,
  VideoStoryblok,
} from '../../../types/generated-storyblok';
import { ExpandableRichText } from '../../Content/ExpandableRichText';
import { Image } from '../../Image';
import PageSection from '../../Layout/PageSection';
import { RichTextLink } from '../../RichTextLink';

interface Props {
  name: string;
  quote?: string;
  headline?: string;
  text: StoryblokRichtext;
  studioGallery?: (ArtistGalleryImageStoryblok | VideoStoryblok)[];
  originalArtworks?: ArtistOriginalWorksStoryblok[];
  intro?: { label?: string; content?: RichtextStoryblok };
  achievements?: { label?: string; content?: RichtextStoryblok };
  highlights?: { label?: string; content?: RichtextStoryblok };
  social?: {
    id: string;
    label: string;
    source: string;
    url: string;
  }[];
}

export const ArtistAboutTab: React.FC<Props> = ({
  achievements,
  headline,
  highlights,
  intro,
  name,
  originalArtworks,
  quote,
  social,
  studioGallery,
  text,
}) => {
  const { t } = useTranslation(['artists']);
  const [emitter] = useAnalyticsEmitter();

  const lightboxItems = React.useMemo(
    () => originalArtworks?.map(storyblokMediaItemToLightboxEntry) || [],
    [originalArtworks],
  );

  const handleSlide = ({
    length,
    position,
    title,
  }: {
    length: number;
    position: number;
    title: string;
  }) => {
    emitter('', 'carousel slide', {
      'carousel title': title,
      'carousel length': length,
      'slide position': position ? position + 1 : 0,
    });
  };

  const mapSocialIcons: Record<string, IconName> = {
    twitter: 'ic_twitter',
    instagram: 'ic_instagram',
    homepage: 'ic_arrow_top_right',
  };

  const handleSocialClick = (source: string) => {
    emitter('', 'social links clicked', {
      source,
      'button_location': 'about tab',
    });
  };

  return (
    <div className="flex flex-col gap-9 md:gap-12">
      {quote ? (
        <PageSection>
          <figure className="mx-auto flex w-full max-w-2xl flex-col gap-y-3 text-center">
            <blockquote className="text-md dark:text-white md:text-lg">
              <p className="text-balance">{quote}</p>
            </blockquote>
          </figure>
        </PageSection>
      ) : null}

      {studioGallery && studioGallery.length > 0 && (
        <PageSection fullWidth>
          <ArtistImageGallery
            images={studioGallery.map((item) => {
              if (item.component === 'Video') {
                const video = item as StoryblokVideo;
                return { ...video, component: 'Video' };
              }
              const image = item as GalleryImage;
              return { ...image, component: 'image' };
            })}
            onClick={(properties: MultiMediaClickOptions) => {
              TrackMultiMediaClick({
                ...properties,
                page: ARTIST_PAGE_TYPE,
                pageTitle: name,
                context: 'gallery',
              });
            }}
            onSlide={(index) => {
              handleSlide({
                title: 'artist image gallery',
                position: index,
                length: studioGallery.length,
              });
            }}
          />
        </PageSection>
      )}

      <PageSection>
        <div className="mx-auto flex w-full max-w-md flex-col gap-y-6">
          {headline ? (
            <p className="text-balance text-center text-md md:text-lg">
              {headline}
            </p>
          ) : null}
          <ExpandableRichText
            charCount={600}
            onClick={() => {
              emitter('', 'rich text expended', {
                artist: name,
              });
            }}
            text={text}
          />
        </div>
      </PageSection>

      {originalArtworks && originalArtworks.length > 0 && (
        <PageSection>
          <Lightbox items={lightboxItems}>
            {(lightbox) => (
              <Grid className="mx-auto max-w-3xl gap-y-6" columns={2}>
                {originalArtworks.map((item, i) => {
                  if (item.image) {
                    return (
                      <button
                        key={item._uid}
                        className="group"
                        onClick={() => {
                          lightbox.openOnSlide(i);
                          emitter('', 'artist media clicked', {
                            'section': 'originalArtworks',
                            'type': 'image',
                            'position': i,
                            'title': item.image?.title,
                          });
                        }}
                        type="button"
                      >
                        <figure className="flex w-full flex-col gap-3">
                          <Image
                            alt={item.image.alt || ''}
                            eagerLoading={i === 0}
                            focus={item.image.focus}
                            hoverEffect
                            shape="rounded"
                            size={{
                              sm: { ratio: 'intrinsic', span: 6 },
                              md: { ratio: 'intrinsic', span: 4 },
                              lg: { ratio: 'intrinsic', span: 3 },
                            }}
                            src={item.image.filename}
                          />

                          <figcaption className="text-xs text-neutral-4">
                            {render(item.title, {
                              markResolvers: { link: RichTextLink },
                            })}

                            {item.overline ? <p>{item.overline}</p> : null}
                          </figcaption>
                        </figure>
                      </button>
                    );
                  }

                  return null;
                })}
              </Grid>
            )}
          </Lightbox>
        </PageSection>
      )}

      <PageSection>
        <div className="mx-auto flex w-full max-w-md flex-col gap-y-6">
          {intro && isStoryblokRichTextPresent(intro.content) ? (
            <div className="flex w-full flex-col gap-y-2">
              {intro.label ? (
                <p className="font-bold">{t('bioTitle')}</p>
              ) : null}
              {renderStoryblokRichText(intro.content, {
                paragraphClassName: 'pb-0',
              })}
            </div>
          ) : null}

          {achievements && isStoryblokRichTextPresent(achievements.content) ? (
            <div className="flex w-full flex-col gap-y-2">
              {achievements.label ? (
                <p className="font-bold">{t(achievements.label)}</p>
              ) : null}
              {renderStoryblokRichText(achievements.content, {
                paragraphClassName: 'pb-0',
              })}
            </div>
          ) : null}

          {highlights && isStoryblokRichTextPresent(highlights.content) ? (
            <div className="flex w-full flex-col gap-y-2">
              {highlights.label ? (
                <p className="font-bold">{t(highlights.label)}</p>
              ) : null}
              {renderStoryblokRichText(highlights.content, {
                paragraphClassName: 'pb-0',
              })}
            </div>
          ) : null}

          {social?.length ? (
            <div className="flex w-full flex-col space-y-2">
              <p className="font-bold">{t('followUpTitle')}</p>
              <div className="flex flex-col items-start gap-y-1">
                {social.map((item) => {
                  const icon = mapSocialIcons[item.source];
                  return (
                    <a
                      key={item.id}
                      className="link inline-flex space-x-1 overflow-hidden"
                      href={item.url}
                      onClick={() => handleSocialClick(item.source)}
                      rel="noreferrer"
                      target="_blank"
                    >
                      <span className="shrink-0">
                        <Icon name={icon} />
                      </span>
                      <span className="flex-1 truncate">{item.label}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </PageSection>
    </div>
  );
};
